import { useState } from 'react';
import { QrConfiguration } from './types';
import { ContentManagerRequest } from 'api/ContentManagerRequest';

const getLinkUrl = (redirectUrl: string) =>
    redirectUrl.replace('source=qr', 'source=link');

interface Props {
    config: QrConfiguration;
    onClose: () => void;
}

const QrImagePanel = ({ config, onClose }: Props) => {
    const [size, setSize] = useState(256);
    const [format, setFormat] = useState<'png' | 'jpeg' | 'svg'>('png');
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const handleGenerate = async () => {
        setError('');
        setImageUrl(null);
        setLoading(true);
        try {
            const resp = await ContentManagerRequest.get(
                `/api/qr/${config.slug}/image`,
                { params: { size, img_format: format }, responseType: 'blob' }
            );
            const url = URL.createObjectURL(resp.data);
            setImageUrl(url);
        } catch (err: any) {
            const detail = err?.response?.data?.detail ?? `Error ${err?.response?.status ?? ''}: no se pudo generar la imagen QR.`;
            setError(detail);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (!imageUrl) return;
        const a = document.createElement('a');
        a.href = imageUrl;
        a.download = `qr-${config.slug}-${size}px.${format}`;
        a.click();
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(getLinkUrl(config.redirect_url)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="card mt-3">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Generar imagen QR — <code>{config.slug}</code></h6>
                <button className="btn btn-sm btn-light" onClick={onClose}>&times;</button>
            </div>
            <div className="card-body">
                <div className="mb-3 d-flex align-items-center">
                    <button className="btn btn-outline-secondary btn-sm mr-2" onClick={handleCopyLink}>
                        <i className="fa fa-copy mr-1"></i> {copied ? '¡Copiado!' : 'Copiar link directo'}
                    </button>
                    <small className="text-muted"><code>{getLinkUrl(config.redirect_url)}</code></small>
                </div>

                <div className="row align-items-end mb-3">
                    <div className="col-md-4">
                        <label>Tamaño (px)</label>
                        <input
                            type="range"
                            className="form-control-range"
                            min={64} max={2048} step={64}
                            value={size}
                            onChange={e => setSize(Number(e.target.value))}
                        />
                        <small className="text-muted">{size}px</small>
                    </div>
                    <div className="col-md-3">
                        <label>Formato</label>
                        <select className="form-control" value={format} onChange={e => setFormat(e.target.value as 'png' | 'jpeg' | 'svg')}>
                            <option value="png">PNG</option>
                            <option value="jpeg">JPEG</option>
                            <option value="svg">SVG</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
                            {loading ? 'Generando...' : 'Generar'}
                        </button>
                    </div>
                </div>

                {error && <div className="alert alert-danger">{error} <button className="btn btn-link btn-sm p-0" onClick={handleGenerate}>Reintentar</button></div>}

                {imageUrl && (
                    <div className="text-center">
                        <img
                            src={imageUrl}
                            alt="QR Code"
                            style={{ maxWidth: 512, maxHeight: 512, border: '1px solid #dee2e6', padding: 8 }}
                        />
                        <div className="mt-3">
                            <button className="btn btn-success" onClick={handleDownload}>
                                <i className="fa fa-download mr-1"></i> Descargar imagen
                            </button>
                        </div>
                        <small className="text-muted d-block mt-2">
                            QR escanea a: <code>{config.redirect_url}</code>
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QrImagePanel;
